// Final copy for the Matrix Results Page v2 (spec approved Oct 13, 2026). Word for word; do not edit.

// Table A — per-symptom dynamic text: Headline Phrase, Verb, Short Form (P.S.)
export const SYMPTOM_COPY = {
  same_eating_gaining: { phrase: "gaining weight on the same food as always", verb: "starts", short: "the weight gain" },
  cravings: { phrase: "the cravings you can't shut off", verb: "start", short: "your cravings" },
  calorie_cut_nothing: { phrase: "the reason cutting calories isn't working", verb: "starts", short: "the stalled scale" },
  hot_flashes: { phrase: "your hot flashes", verb: "start", short: "your hot flashes" },
  night_sweats: { phrase: "waking up drenched", verb: "starts", short: "the night sweats" },
  mood: { phrase: "the mood swings and short fuse", verb: "start", short: "your mood" },
  low_libido: { phrase: "your low libido", verb: "starts", short: "your libido" },
  brain_fog: { phrase: "the brain fog and lost words", verb: "start", short: "the brain fog" },
  belly_fat: { phrase: "the belly fat that won't budge", verb: "starts", short: "your belly fat" },
  clothes_fit: { phrase: "clothes fitting differently at the same weight", verb: "starts", short: "the way your clothes fit" },
  crash_after_eating: { phrase: "the crash an hour after you eat", verb: "starts", short: "the after-meal crash" },
  lost_tone: { phrase: "losing your muscle tone", verb: "starts", short: "your muscle tone" },
  losing_strength: { phrase: "losing your strength", verb: "starts", short: "your strength" },
  achy_joints: { phrase: "your achy, creaky joints", verb: "start", short: "your joints" },
  cant_fall_asleep: { phrase: "the trouble falling asleep", verb: "starts", short: "your trouble falling asleep" },
  awake_2am: { phrase: "waking up wide awake at 2 or 3am", verb: "starts", short: "those 2am wake-ups" },
  fatigue: { phrase: "the fatigue that sleep doesn't fix", verb: "starts", short: "your fatigue" },
  sore_for_days: { phrase: "being sore for days after a workout", verb: "starts", short: "the soreness" },
  bloated: { phrase: "the bloating, no matter what you eat,", verb: "starts", short: "the bloating" },
  food_intolerance: { phrase: "foods that suddenly bother you", verb: "start", short: "the foods that bother you now" },
  winded: { phrase: "getting winded from things that used to be easy", verb: "starts", short: "getting winded" },
  stairs_hills: { phrase: "getting winded on stairs and hills", verb: "starts", short: "stairs and hills" },
};

// Table C — "Here's what next year looks like if nothing changes", by START HERE system
export const IF_NOTHING_CHANGES = {
  nutrition: [
    "Let's be honest with each other for a minute.",
    "You'll keep eating the way that used to work. It won't work the same way now.",
    "You'll try cutting carbs, then cutting fat, then skipping meals. You'll be hungry, tired and short-tempered, and the results won't last.",
    "You'll keep spending money on plans built for a body you don't have anymore.",
    "And the cravings, the crashes and the weight will keep coming back, because your food is fighting the other six systems instead of helping them.",
  ],
  hormonal: [
    "Let's be honest with each other for a minute.",
    "You'll keep putting up with the hot flashes, the night sweats, the short fuse and the nights you don't sleep.",
    "You'll tell yourself it's just this stage and it will pass. For some women it eases. For a lot of women it drags on for years.",
    "You'll try a supplement, then another one, because nobody looked at the whole picture.",
    "And because hormones touch your sleep, your mood, your weight and your muscle, every other system pays the price while you wait.",
  ],
  metabolic: [
    "Let's be honest with each other for a minute.",
    "You'll try the next diet. You'll eat less, move more and do everything right. The scale won't care.",
    "You'll buy the next size up and tell yourself it's only for now.",
    "You'll spend more money on things that each fix one piece, while your metabolism keeps playing by its new rules.",
    "And every year you wait, your body has a little less muscle to burn with. Less muscle means a slower metabolism. A slower metabolism makes every pound harder to move.",
  ],
  resistance: [
    "Let's be honest with each other for a minute.",
    "You'll keep feeling a little weaker carrying the groceries, getting up off the floor, opening the jar.",
    "You'll do more cardio, because that's what you were told, and wonder why you look softer instead of stronger.",
    "After 40, women lose muscle every year unless they work to keep it, and the loss speeds up around menopause. Less muscle means a slower metabolism and joints with less support.",
    "And the \"who is this?\" feeling in the mirror gets a little stronger every year.",
  ],
  recovery: [
    "Let's be honest with each other for a minute.",
    "You'll keep waking up at 2 or 3am and lying there, or not falling asleep at all.",
    "You'll run on coffee by day and something to wind down by night.",
    "You'll push through workouts on no sleep and wonder why you're sore for days.",
    "Poor sleep raises the stress hormone that tells your body to store fat around your middle, and it makes the cravings louder. Every other system gets harder, because you're trying to rebuild on an empty tank.",
  ],
  gut: [
    "Let's be honest with each other for a minute.",
    "You'll keep feeling bloated no matter what you eat.",
    "You'll cut out one food, then another, then another, until eating feels like a guessing game.",
    "You'll plan your day around how your stomach feels.",
    "And because your gut affects your energy, your mood and your cravings, it keeps pulling the other systems down with it.",
  ],
  cardiovascular: [
    "Let's be honest with each other for a minute.",
    "You'll keep getting winded from things that used to be easy: the stairs, the hill, keeping up on a walk with a friend.",
    "You'll start choosing the elevator and the closer parking spot without noticing you're doing it.",
    "The less you move, the harder moving gets. It's a slow slide, and it's easy to miss until it's steep.",
    "And your heart and lungs carry every other system. When they fall behind, everything feels harder.",
  ],
};

// Table D — "A note on hormones", by HRT answer (record.hrt_status)
export const HORMONE_NOTE = {
  no: "Hormonal is one of your active systems. In the full picture it accounts for 20%. Hormone support helps, and it's the one system that needs labs and a licensed provider to get right. It just doesn't hold on its own when the other six are working against it.",
  on_hrt:
    "You told us you're already on hormone therapy. Good. That's one system getting real support. But hormones are one system of seven, and HRT doesn't hold on its own when the other six are working against it. That's why so many women on HRT still feel like something is missing. On your call, Sandra will look at which of the other six still need work.",
  considering:
    "You told us you're considering hormone therapy. That's a decision for a licensed provider and a set of labs, and if and when you want that, we'll connect you with our vetted partners. Either way, hormones are one system of seven. Whatever you decide, the other six decide whether it holds.",
  tried_failed:
    "You told us you tried hormone therapy and it didn't work. That doesn't have to be the end of the story. Hormones are one system of seven, and when the other six are working against them, hormone therapy struggles to hold. On your call, Sandra will look at what else was going on. And if and when you want a fresh look at the hormone side, our vetted medical partners can help.",
};

export const WHY_ONE_COACH = [
  "Most women over 40 already have help. A doctor for one thing. A trainer or a class for another. An app for food. A supplement for sleep.",
  "The help isn't the problem. The problem is that nobody is in charge of how it fits together.",
  "So the pieces fight. Your training fights your sleep. Your diet fights your hormones. And you're the one left guessing whose advice to follow.",
];
export const WHY_ONE_COACH_CLOSING =
  "One coach who owns all seven systems can see that happening and change the order before you lose another month. That's the whole idea behind Full Stack Coaching.";

export const DOCTOR_CALLOUT = {
  lead: "Even with your lab work in hand, if your doctor prescribes your medication without first asking (at a minimum) about",
  items: ["Your diet", "Your exercise routine", "Your sleep", "Your digestion", "The stress in your life"],
  punch: "Then you don't have a doctor, you have a drug dealer.",
};

export const HOW_FIXED_NEW_PARAGRAPH =
  "Not all of my clients need hormone optimization or metabolic support, but for the ones that do, we have the right hand-picked team partners of medical, lab and pharmacy providers. And not all providers get perimenopause, menopause and post-menopause, be they your family doctor, a local wellness clinic or local hormone specialist. Our vetted medical partners do menopause, this is all they do, and they have been doing it for nearly 15 years. They have been helping women anywhere in the 3 stages of menopause, long before it was a real, evidence-based option. And trust me, with this, experience matters a great deal.";

export const GUARANTEE = {
  label: "Our Promise",
  heading: "The No More Wasted Time Guarantee",
  intro: "You've already lost enough time to things that didn't work. So if you become a Full Stack Coaching client, here's our promise.",
  items: [
    { label: "The Right Fit Window.", text: "Give it 28 days. If it isn't the right fit, you get your money back, minus the cost of building your plan." },
    { label: "Two Systems Better.", text: "We re-score all systems at day 90. If at least two of your systems haven't measurably improved, you get 3 additional months of coaching at no charge." },
  ],
};

export const TESTIMONIALS = [
  {
    quote:
      "Sandra went above and beyond to help my son and I reach our fitness and health goals. We were not active individuals and needed to make big changes.",
    by: "Jessica Lewis, Sharpsburg, Georgia",
  },
  {
    quote:
      "Truly had the BEST experience with Sassy Fitness. Everything from my nutrition to training was fully customized for my goals, and I saw the most incredible results in not only how I looked, but most importantly how I felt.",
    by: "Alexis Gregory, Inlet Beach, Florida",
  },
  {
    quote:
      "I love training with Sandra! She makes the experience something to look forward to. She is passionate about her clients and sets such a great example of the way she lives life.",
    by: "Caron Watson, Panama City Beach, Florida",
  },
];

export const CTA_LABEL = "Book My Free Matrix Review";
export const CTA_SUBLINE = "30 minutes with Sandra. She'll go over your results with you.";
